import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { liveChallengesApi } from '@/services/liveChallenges';
import { useLiveChallenge } from '@/hooks/useLiveChallenge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

const PODIUM_STYLES = {
  0: { height: 'h-32', bg: 'bg-[#FFD700]', label: '1º', textColor: 'text-neutral-darker' },
  1: { height: 'h-24', bg: 'bg-neutral-300', label: '2º', textColor: 'text-neutral-darker' },
  2: { height: 'h-20', bg: 'bg-[#CD7F32]', label: '3º', textColor: 'text-white' }
};

const MEDAL_BG = ['bg-[#FFD700]', 'bg-neutral-300', 'bg-[#CD7F32]', 'bg-neutral-lightest'];

function PodiumPlace({ entry, place }) {
  const style = PODIUM_STYLES[place] || PODIUM_STYLES[2];

  if (!entry) return null;

  return (
    <div className="flex flex-col items-center">
      <p className="mb-2 text-sm font-bold text-foreground">{entry.user_name || entry.name || 'Participante'}</p>
      <p className="mb-1 text-xs font-semibold text-primary">{entry.total_score ?? entry.score ?? 0} pts</p>
      <div className={`flex w-24 items-end justify-center rounded-t-lg ${style.bg} ${style.height}`}>
        <span className={`pb-2 text-2xl font-extrabold ${style.textColor}`}>{style.label}</span>
      </div>
    </div>
  );
}

export default function FinalView({ challengeId }) {
  const navigate = useNavigate();
  const { leaderboard: contextLeaderboard } = useLiveChallenge();
  const [fullLeaderboard, setFullLeaderboard] = useState([]);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        const data = await liveChallengesApi.leaderboard(challengeId, 100);
        const items = Array.isArray(data?.data) ? data.data : [];
        const normalized = items.map(it => {
          const a = it.attributes || {};
          return {
            id: it.id,
            user_id: a.user?.id,
            user_name: a.user?.name,
            name: a.user?.name,
            total_score: a['total-score'] ?? a.total_score ?? 0,
            rank: a.rank
          };
        });
        setFullLeaderboard(normalized);
      } catch {
        setFullLeaderboard(contextLeaderboard || []);
      }
    };
    fetchLeaderboard();
  }, [challengeId, contextLeaderboard]);

  const leaderboard = fullLeaderboard.length > 0 ? fullLeaderboard : contextLeaderboard || [];
  const podium = leaderboard.slice(0, 3);
  const showPodium = podium.length >= 2;

  const podiumDisplay = [podium[1], podium[0], podium[2]];
  const podiumPlaces = [1, 0, 2];

  if (!showPodium) {
    return (
      <div className="mx-auto flex max-w-2xl flex-col gap-6 py-8">
        <header className="text-center">
          <h2 className="m-0 text-2xl font-bold text-foreground">Resultado Final</h2>
          <p className="mt-1 text-sm text-neutral">{podium.length === 0 ? 'Sem participantes — sessão sem resultados.' : 'Sessão finalizada com 1 participante.'}</p>
        </header>

        <div className="flex flex-col gap-3">
          <h3 className="m-0 text-lg font-semibold text-foreground">Classificação</h3>
          {leaderboard.length > 0 ? (
            <Card
              border
              orientation="vertical"
              className="px-0 py-0"
            >
              <ul className="m-0 list-none pl-0">
                {leaderboard.map((entry, idx) => {
                  const medalBg = idx < 3 ? MEDAL_BG[idx] : MEDAL_BG[3];
                  const medalText = idx < 3 ? 'text-white' : 'text-neutral-dark';
                  return (
                    <li
                      key={entry.user_id || idx}
                      className="flex items-center justify-between border-b border-solid border-neutral-lightest px-4 py-3 last:border-0"
                    >
                      <div className="flex items-center gap-3">
                        <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${medalBg} ${medalText}`}>{idx + 1}</span>
                        <span className="text-sm font-medium text-foreground">{entry.user_name || entry.name || `Participante ${idx + 1}`}</span>
                      </div>
                      <span className="text-sm font-bold text-primary">{entry.total_score ?? entry.score ?? 0} pts</span>
                    </li>
                  );
                })}
              </ul>
            </Card>
          ) : (
            <p className="m-0 text-center text-sm text-neutral-light">Nenhum participante registrou pontos.</p>
          )}
        </div>

        <Button
          type="button"
          variant="dark"
          size="lg"
          fullWidth
          onClick={() => navigate('/admin/desafio-ao-vivo')}
        >
          Encerrar painel
        </Button>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-8 py-8 md:grid-cols-2 md:gap-12">
      {/* Coluna 1: Pódio */}
      <div className="flex flex-col items-center gap-6">
        <h2 className="m-0 text-2xl font-bold text-foreground">Resultado Final</h2>

        <div className="flex items-end justify-center gap-4">
          {podiumDisplay.map((entry, idx) => (
            <PodiumPlace
              key={entry?.user_id || idx}
              entry={entry}
              place={podiumPlaces[idx]}
            />
          ))}
        </div>

        <Button
          type="button"
          variant="dark"
          size="lg"
          fullWidth
          onClick={() => navigate('/admin/desafio-ao-vivo')}
        >
          Encerrar painel
        </Button>
      </div>

      {/* Coluna 2: Classificação geral */}
      <div className="flex flex-col gap-3">
        <h3 className="m-0 text-lg font-semibold text-foreground">Classificação Geral</h3>
        {leaderboard.length > 0 ? (
          <Card
            border
            orientation="vertical"
            className="px-0 py-0"
          >
            <ul className="m-0 max-h-[520px] list-none overflow-y-auto pl-0">
              {leaderboard.map((entry, idx) => {
                const medalBg = idx < 3 ? MEDAL_BG[idx] : MEDAL_BG[3];
                const medalText = idx < 3 ? 'text-white' : 'text-neutral-dark';
                return (
                  <li
                    key={entry.user_id || idx}
                    className="flex items-center justify-between border-b border-solid border-neutral-lightest px-4 py-3 last:border-0"
                  >
                    <div className="flex items-center gap-3">
                      <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${medalBg} ${medalText}`}>{idx + 1}</span>
                      <span className="text-sm font-medium text-foreground">{entry.user_name || entry.name || `Participante ${idx + 1}`}</span>
                    </div>
                    <span className="text-sm font-bold text-primary">{entry.total_score ?? entry.score ?? 0} pts</span>
                  </li>
                );
              })}
            </ul>
          </Card>
        ) : (
          <p className="m-0 text-sm text-neutral-light">Sem participantes.</p>
        )}
      </div>
    </div>
  );
}
